import { AddEncounterDialog } from '@/components/molecules/AddEncounterDialog';
import { RecordListScroll } from '@liza/ui/components/RecordListScroll';
import { useInfiniteRecordList } from '@liza/ui/hooks/useInfiniteRecordList';
import { useTRPC, useTRPCClient } from '@/utils/trpc';
import type { PatientAdminApiRouterOutputs } from '@liza/patient-admin-api';
import { EncountersList } from '@liza/ui/components/records/EncountersList';
import {
  type RecordsListFilters,
  defaultFilters,
  RecordsListControls,
} from '@liza/ui/components/records/RecordsListControls';
import { useRecordPromotionStatus } from '@liza/ui/hooks/useRecordPromotionStatus';
import { Toggle } from '@liza/ui/components/ui/toggle';
import { ACUTE_ENCOUNTER_CLASS_SEARCH, classifyAcuteEncounter } from '@liza/fhir-utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import type { DetailPanelView } from '../types';

type Patient = PatientAdminApiRouterOutputs['patient']['get']['patient'];

type Props = {
  onViewChange: (view: DetailPanelView | null) => void;
  patient: Patient;
  selectedId?: string;
};

export const Encounters = ({ onViewChange, patient, selectedId }: Props): React.ReactElement => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<RecordsListFilters>(defaultFilters);
  const handleFiltersChange = useDebouncedCallback(setFilters, 300);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [acuteOnly, setAcuteOnly] = useState(false);

  // "ER & hospital only" narrows the server search to the acute classes; the classifier then drops the
  // `UNK` rows that carry no ED marker, so the list still pages but shows only ED visits and stays. The
  // handful of rows whose only ED marker is their type text (Epic-local classes) need the second,
  // text-based search, which a paginated list cannot union in; the episode views fetch both in full.
  const listOptions = {
    ownership: filters.ownership,
    searchParams: { 'type:text': filters.searchQuery, ...(acuteOnly ? ACUTE_ENCOUNTER_CLASS_SEARCH : {}) },
    status: filters.status,
  };

  const {
    items: fetchedEncounters,
    query,
    bottomSentinelRef,
  } = useInfiniteRecordList({
    queryKey: trpc.patient.encounter.list.queryKey({ medplumPatientId: patient.medplumId, options: listOptions }),
    queryFn: async ({ offset, count }) => {
      const res = await trpcClient.patient.encounter.list.query({
        medplumPatientId: patient.medplumId,
        options: listOptions,
        offset,
        count,
      });
      return { items: res.encounters, hasMore: res.hasMore };
    },
  });

  const encounters = useMemo(
    () => (acuteOnly ? fetchedEncounters.filter((encounter) => classifyAcuteEncounter(encounter)) : fetchedEncounters),
    [acuteOnly, fetchedEncounters],
  );

  const { mutateAsync: promoteEncounter } = useMutation(
    trpc.patient.encounter.promote.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(
          trpc.patient.encounter.list.queryFilter({ medplumPatientId: patient.medplumId }),
        );
        void queryClient.invalidateQueries(
          trpc.patient.documentReference.list.queryFilter({ medplumPatientId: patient.medplumId }),
        );
      },
    }),
  );

  const { promote, getPromotionStatus } = useRecordPromotionStatus();

  const handlePromotionClick = useCallback(
    (id: string): void => {
      void promote(id, async () => promoteEncounter({ medplumPatientId: patient.medplumId, sourceEncounterId: id }));
    },
    [promoteEncounter, patient.medplumId, promote],
  );

  return (
    <div className="flex flex-col gap-2 p-2">
      <RecordsListControls
        title={t('encounters')}
        searchPlaceholder={t('searchEncounters')}
        onFiltersChange={handleFiltersChange}
        onAdd={() => setIsAddOpen(true)}
        addLabel={t('addEncounter')}
        addTestId="add-encounter-button"
      />
      <Toggle
        variant="outline"
        size="sm"
        pressed={acuteOnly}
        onPressedChange={setAcuteOnly}
        aria-label={t('acuteEncountersOnly')}
        className="self-start text-xs"
        data-testid="acute-encounters-toggle"
      >
        {t('acuteEncountersOnly')}
      </Toggle>
      <AddEncounterDialog
        medplumPatientId={patient.medplumId}
        organizationId={patient.organizationId}
        isOpen={isAddOpen}
        onOpenChange={setIsAddOpen}
      />
      <RecordListScroll
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        isFetchingNextPage={query.isFetchingNextPage}
        bottomSentinelRef={bottomSentinelRef}
      >
        <EncountersList
          encounters={encounters}
          onSelectEncounter={(id) => onViewChange({ type: 'encounter-detail', id })}
          selectedEncounterId={selectedId}
          onPromotionClick={filters.ownership === 'external' ? handlePromotionClick : undefined}
          getPromotionStatus={getPromotionStatus}
        />
      </RecordListScroll>
    </div>
  );
};
